import { getStateInvariantViolations } from "../invariants/game-state-invariants";
import { applyAdvancePhase } from "../transitions/advance-phase";
import { applyDevelopLand } from "../transitions/develop-land";
import { applyDeployArmy } from "../transitions/deploy-army";
import { applyDeployCommander } from "../transitions/deploy-commander";
import { applyDisbandArmy } from "../transitions/disband-army";
import { applyDisbandCommander } from "../transitions/disband-commander";
import { applyDiscardArmyForUpkeep } from "../transitions/discard-army-for-upkeep";
import { applyFoundLand } from "../transitions/found-land";
import { applyReplaceCommander } from "../transitions/replace-commander";
import { applyAttack } from "../transitions/resolve-attack";
import { applyPlayEvent } from "../transitions/play-event";
import { applyEndTurn } from "../transitions/public-beta-turn";
import { reconcileNoTerritoryWarning } from "../transitions/territory";
import type {
  ActionValidationFailure,
  ApplyActionResult,
  EngineContext,
  GameAction,
  GameState,
} from "../types";
import { validateAction } from "../validation/validate-action";

function rejected(state: GameState, failure: ActionValidationFailure): ApplyActionResult {
  return { ok: false, state, events: [], failure };
}

function cloneGameAction(action: GameAction): GameAction {
  return { ...action };
}

function contextFailure(
  state: Readonly<GameState>,
  context: EngineContext,
): ActionValidationFailure | null {
  if (
    state.rulesVersion !== context.rulesVersion ||
    state.cardDataVersion !== context.cardDataVersion
  ) {
    return {
      code: "CONTEXT_VERSION_MISMATCH",
      message: "Engine context versions must match the versions recorded in game state.",
      path: "rulesVersion",
      details: {
        stateRulesVersion: state.rulesVersion,
        contextRulesVersion: context.rulesVersion,
        stateCardDataVersion: state.cardDataVersion,
        contextCardDataVersion: context.cardDataVersion,
      },
    };
  }

  return null;
}

export function applyAction(
  state: GameState,
  action: GameAction,
  context: EngineContext,
): ApplyActionResult {
  const mismatchedContext = contextFailure(state, context);
  if (mismatchedContext !== null) {
    return rejected(state, mismatchedContext);
  }

  const validation = validateAction(state, action);
  if (!validation.valid) {
    return rejected(state, validation.failure);
  }

  const previousRecord = state.actionLog[state.actionLog.length - 1];
  const actionSequence = (previousRecord?.sequence ?? 0) + 1;
  const transition =
    action.type === "advance-phase"
      ? applyAdvancePhase(state, actionSequence)
      : action.type === "attack"
        ? applyAttack(state, action, actionSequence)
        : action.type === "found-land" &&
            (state.schemaVersion === 2 ||
              state.schemaVersion === 3 ||
              state.schemaVersion === 4 ||
              state.schemaVersion === 5)
          ? applyFoundLand(state, action, actionSequence)
          : action.type === "develop-land" &&
              (state.schemaVersion === 2 ||
                state.schemaVersion === 3 ||
                state.schemaVersion === 4 ||
                state.schemaVersion === 5)
            ? applyDevelopLand(state, action, actionSequence)
            : action.type === "discard-army-for-upkeep" &&
                (state.schemaVersion === 3 ||
                  state.schemaVersion === 4 ||
                  state.schemaVersion === 5)
              ? applyDiscardArmyForUpkeep(state, action, actionSequence)
              : action.type === "disband-army" &&
                  (state.schemaVersion === 2 ||
                    state.schemaVersion === 3 ||
                    state.schemaVersion === 4 ||
                    state.schemaVersion === 5)
                ? applyDisbandArmy(state, action, actionSequence)
                : action.type === "disband-commander" &&
                    (state.schemaVersion === 2 ||
                      state.schemaVersion === 3 ||
                      state.schemaVersion === 4 ||
                      state.schemaVersion === 5)
                  ? applyDisbandCommander(state, action, actionSequence)
                  : action.type === "deploy-commander" &&
                      (state.schemaVersion === 3 ||
                        state.schemaVersion === 4 ||
                        state.schemaVersion === 5)
                    ? applyDeployCommander(state, action, actionSequence)
                    : action.type === "deploy-army" &&
                        (state.schemaVersion === 3 ||
                          state.schemaVersion === 4 ||
                          state.schemaVersion === 5)
                      ? applyDeployArmy(state, action, actionSequence)
                      : action.type === "replace-commander" &&
                          (state.schemaVersion === 3 ||
                            state.schemaVersion === 4 ||
                            state.schemaVersion === 5)
                        ? applyReplaceCommander(state, action, actionSequence)
                        : action.type === "play-event" && state.schemaVersion === 5
                          ? applyPlayEvent(state, action, actionSequence)
                          : action.type === "end-turn" && state.schemaVersion === 5
                            ? applyEndTurn(state, actionSequence)
                            : null;

  if (transition === null) {
    return rejected(state, {
      code: "ACTION_NOT_IMPLEMENTED",
      message: "This action has no transition implementation.",
      path: "type",
      details: { actionType: action.type },
    });
  }

  const territory = reconcileNoTerritoryWarning(
    transition.state,
    actionSequence,
    transition.events.length,
  );
  const emittedEvents = [...transition.events, ...territory.events];
  const nextState: GameState = {
    ...territory.state,
    actionLog: [
      ...territory.state.actionLog,
      { sequence: actionSequence, playerId: action.playerId, action: cloneGameAction(action) },
    ],
    eventQueue: [...territory.state.eventQueue, ...emittedEvents],
  };

  const invariantViolation = getStateInvariantViolations(nextState)[0];
  if (invariantViolation !== undefined) {
    return rejected(state, {
      code: "INVALID_STATE",
      message: invariantViolation.message,
      path: invariantViolation.path,
      details: { invariantCode: invariantViolation.code },
    });
  }

  return { ok: true, state: nextState, events: emittedEvents };
}
